import type { AnalysisResult } from "@/lib/resultRouter";

/**
 * Grounded Script Generator
 * All content is anchored to actual analyzer output:
 * exact phrase, actual pattern, interpretation, score band, nuance.
 */

export type Platform = "tiktok" | "youtube" | "x" | "carousel";

export const PLATFORM_OPTIONS: { key: Platform; label: string; icon: string }[] = [
  { key: "tiktok", label: "TikTok / Reels", icon: "🎵" },
  { key: "youtube", label: "YT Shorts", icon: "▶️" },
  { key: "x", label: "X / Thread", icon: "𝕏" },
  { key: "carousel", label: "Carousel", icon: "📸" },
];

const formatPattern = (p: string) =>
  p.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

type ScoreBand = "low" | "moderate" | "high";

const getScoreBand = (score: number): ScoreBand => {
  if (score >= 7) return "high";
  if (score >= 4) return "moderate";
  return "low";
};

const severityLabel = (band: ScoreBand): string => {
  if (band === "high") return "high-severity signal";
  if (band === "moderate") return "moderate / ambiguous signal";
  return "low-risk / neutral";
};

const severityEmoji = (band: ScoreBand): string => {
  if (band === "high") return "🔴";
  if (band === "moderate") return "🟡";
  return "🟢";
};

/** Script-type-aware hashtags — varied per type and platform, deduplicated */
function buildHashtags(
  analysis: AnalysisResult,
  platform: Platform,
  scriptType: "expose" | "greyrock" | "educational" = "expose"
): string {
  const p = analysis.primary_pattern.toLowerCase().replace(/_/g, "");
  const band = getScoreBand(analysis.score);

  // Base tag varies by script type to avoid identical hashtag blocks
  const typeTags: Record<string, string[]> = {
    expose: [`#${p}`, band === "high" ? "#accountability" : "#awareness"],
    greyrock: [`#${p}`, "#boundaries", "#healthyresponse"],
    educational: [`#${p}`, "#communication", "#patterns"],
  };

  const tags = [...(typeTags[scriptType] || typeTags.expose)];

  // Add secondary pattern if distinct
  if (analysis.secondary_pattern) {
    const s = analysis.secondary_pattern.toLowerCase().replace(/_/g, "");
    if (s !== p && !tags.includes(`#${s}`)) tags.push(`#${s}`);
  }

  // Deduplicate
  const unique = [...new Set(tags)];

  // Platform-aware density
  if (platform === "x") return unique.slice(0, 2).join(" ");
  if (platform === "carousel") return unique.slice(0, 3).join(" ");
  return unique.slice(0, 3).join(" ");
}

// ─── Hook Variants ───
export function generateHookVariants(rawText: string, analysis: AnalysisResult, platform: Platform = "tiktok"): { style: string; label: string; text: string }[] {
  const phrase = rawText.slice(0, 80);
  const pattern = formatPattern(analysis.primary_pattern);
  const band = getScoreBand(analysis.score);

  // Platform-adapted hook generation
  if (platform === "tiktok") {
    return [
      { style: "scroll-stop", label: "Scroll-Stop", text: band === "high" ? `This isn't just rude — it's ${pattern.toLowerCase()}.` : band === "moderate" ? `This sounds small, but it's actually ${pattern.toLowerCase()}.` : `This phrase is worth a second look.` },
      { style: "educational", label: "Explainer Open", text: band === "high" ? `Here's how ${pattern.toLowerCase()} works in everyday language.` : `Here's what "${phrase.slice(0, 40)}…" is doing to the conversation.` },
      { style: "pattern", label: "Pattern Lead", text: band === "high" ? `This is ${pattern.toLowerCase()} — and it's a clear signal.` : `This phrase ${analysis.primary_pattern.includes("invalidation") ? "makes your reaction the problem" : "applies indirect pressure instead of making a direct point"}.` },
    ];
  }

  if (platform === "youtube") {
    return [
      { style: "scroll-stop", label: "Opening Line", text: band === "high" ? `Let's talk about what ${pattern.toLowerCase()} actually sounds like.` : `"${phrase.slice(0, 40)}…" — let me show you what this is doing.` },
      { style: "educational", label: "Narrative Open", text: `There's a communication pattern called ${pattern.toLowerCase()}. Here's how it works.` },
      { style: "pattern", label: "Direct Breakdown", text: band === "high" ? `This phrase is a textbook example of ${pattern.toLowerCase()}.` : `This phrase sits in ambiguous territory — and that's exactly why it's worth understanding.` },
    ];
  }

  if (platform === "x") {
    return [
      { style: "scroll-stop", label: "Thread Opener", text: band === "high" ? `"${phrase.slice(0, 50)}…"\n\nThis is ${pattern.toLowerCase()}. Here's why it matters →` : `"${phrase.slice(0, 50)}…"\n\nThis phrase is doing more than you think →` },
      { style: "educational", label: "Statement", text: `${pattern} is one of the most overlooked communication patterns. A thread:` },
      { style: "pattern", label: "Tight Take", text: band === "high" ? `"${phrase.slice(0, 50)}…" = ${pattern.toLowerCase()}. Full stop.` : `"${phrase.slice(0, 50)}…" — sounds harmless, functions as ${pattern.toLowerCase()}.` },
    ];
  }

  // carousel
  return [
    { style: "scroll-stop", label: "Slide 1 Title", text: band === "high" ? `What ${pattern.toLowerCase()} actually looks like` : `Is this actually a problem?` },
    { style: "educational", label: "Subtitle Hook", text: `Understanding ${pattern.toLowerCase()} in everyday conversations` },
    { style: "pattern", label: "Cover Line", text: band === "high" ? `"${phrase.slice(0, 40)}…" — a ${pattern.toLowerCase()} breakdown` : `"${phrase.slice(0, 40)}…" — pattern or overreaction?` },
  ];
}

// ─── Caption Variants ───
export function generateCaptionVariants(rawText: string, analysis: AnalysisResult, platform: Platform = "tiktok"): { style: string; label: string; text: string }[] {
  const pattern = formatPattern(analysis.primary_pattern);
  const band = getScoreBand(analysis.score);
  const nuance = analysis.nuance;

  // Caption maps grounded in actual patterns
  const mechanismMap: Record<string, { core: string; effect: string; compact: string }> = {
    "Triangulation": { core: `"Ask anyone" sounds casual, but it applies vague social pressure instead of making a direct point.`, effect: `This phrase uses outside agreement as leverage. It doesn't prove anything — it just makes disagreement feel unreasonable.`, compact: `Vague outside agreement = social pressure, not proof.` },
    "Social Leverage": { core: `Invoking unnamed others isn't evidence — it's pressure dressed as consensus.`, effect: `This tactic substitutes real reasoning with implied group agreement to make your position feel isolated.`, compact: `"Everyone thinks so" ≠ an actual argument.` },
    "Invalidation": { core: `Calling the reaction the problem is a way to avoid addressing what caused it.`, effect: `Invalidation reframes your emotional response as disproportionate, shifting focus from the behavior to the feeling.`, compact: `The reaction isn't the issue — the cause is.` },
    "Soft Invalidation": { core: `Calling someone "dramatic" shifts focus away from what caused the reaction.`, effect: `This is soft invalidation: the emotion gets framed as unreasonable instead of being addressed.`, compact: `The reaction becomes the issue instead of the behavior.` },
    "Gaslighting": { core: `"That never happened" doesn't invite discussion — it erases your version of events.`, effect: `Gaslighting targets your memory and perception, not just your opinion. The goal is to control which reality counts.`, compact: `Denying shared reality isn't a disagreement — it's control.` },
    "Guilt Induction": { core: `This phrase turns past effort into emotional debt, pressuring you to comply.`, effect: `Guilt induction frames sacrifice as leverage, implying you owe a specific response because of what they've given.`, compact: `Past effort doesn't create present obligation.` },
    "Passive Aggression": { core: `The hostility is there — it's just wrapped in deniability.`, effect: `Passive aggression conveys disapproval indirectly, making it hard to address without sounding like you're overreacting.`, compact: `Indirect hostility with built-in deniability.` },
    "Blame Shift": { core: `The original issue disappears because you're now defending yourself instead.`, effect: `Blame shifting flips accountability so the person raising the concern becomes the one being questioned.`, compact: `You raised the issue — now you're the one explaining.` },
    "Control Framing": { core: `A preference presented as the only reasonable option isn't care — it's control.`, effect: `Control framing disguises demands as logic, concern, or protection, making resistance seem irrational.`, compact: `"For your own good" often means "for my preference."` },
    "Minimization": { core: `Downsizing what happened makes your reaction look disproportionate.`, effect: `Minimization reduces the significance of harmful behavior so concern feels like overreaction.`, compact: `"It wasn't that bad" = your feelings don't count.` },
  };

  const mapped = mechanismMap[pattern];
  const core = mapped?.core || (nuance ? nuance.slice(0, 160) + (nuance.length > 160 ? "." : "") : `This phrase uses ${pattern.toLowerCase()} to shift the dynamic in the conversation.`);
  const effect = mapped?.effect || (band === "high" ? `${pattern} is a clear communication pattern. Here, it's used to ${nuance ? nuance.toLowerCase().slice(0, 100) : "redirect accountability or pressure compliance"}.` : `This sits in ambiguous territory — the ${pattern.toLowerCase()} effect may be careless or strategic, but it's still worth recognizing.`);
  const compact = mapped?.compact || `${pattern} → shifts focus away from the real issue.`;

  if (platform === "tiktok") {
    return [
      { style: "creator", label: "Creator Caption", text: core },
      { style: "educational", label: "Educational Caption", text: effect },
      { style: "minimal", label: "Minimal Caption", text: compact },
    ];
  }

  if (platform === "youtube") {
    return [
      { style: "creator", label: "Description Lead", text: `${core}\n\nPattern: ${pattern} · Score: ${analysis.score.toFixed(1)}/10` },
      { style: "educational", label: "Full Description", text: `${effect}\n\nLearn more about communication patterns at GreyRockAI.app` },
      { style: "minimal", label: "Pinned Comment", text: `${compact} — What would you say back?` },
    ];
  }

  if (platform === "x") {
    return [
      { style: "creator", label: "Tweet", text: core.length > 260 ? core.slice(0, 257) + "…" : core },
      { style: "educational", label: "Thread Post", text: effect.length > 260 ? effect.slice(0, 257) + "…" : effect },
      { style: "minimal", label: "Quote Tweet", text: compact },
    ];
  }

  // carousel
  return [
    { style: "creator", label: "Slide Caption", text: core },
    { style: "educational", label: "Deep Slide", text: effect },
    { style: "minimal", label: "Final Slide", text: `${compact}\n\nSave this for later.` },
  ];
}

// ─── CTA / Title Variants ───
export function generateCTAVariants(rawText: string, analysis: AnalysisResult, platform: Platform = "tiktok"): { style: string; label: string; text: string }[] {
  const pattern = formatPattern(analysis.primary_pattern);
  const band = getScoreBand(analysis.score);

  if (platform === "tiktok") {
    return [
      { style: "engagement", label: "Engagement CTA", text: band === "high" ? `Would you clock this as ${pattern.toLowerCase()}?` : `Have you heard this phrase before?` },
      { style: "product", label: "Product CTA", text: `Run your phrase through GreyRockAI.` },
      { style: "save", label: "Save / Share", text: band === "high" ? `Save this if you've experienced ${pattern.toLowerCase()}.` : `Send this to someone who needs the language for it.` },
    ];
  }

  if (platform === "youtube") {
    return [
      { style: "engagement", label: "End Screen CTA", text: band === "high" ? `Drop your phrase in the comments — I'll tell you what pattern it is.` : `What would you say back? Comment below.` },
      { style: "product", label: "Description CTA", text: `Analyze your own phrase at GreyRockAI.app → link in description.` },
      { style: "save", label: "Subscribe CTA", text: `Subscribe if you want to learn how to decode communication patterns.` },
    ];
  }

  if (platform === "x") {
    return [
      { style: "engagement", label: "Reply Prompt", text: band === "high" ? `RT if you've heard this. Quote with what you'd say back.` : `What would you say back to this?` },
      { style: "product", label: "Product CTA", text: `See how GreyRockAI scores yours → greyrockai.app` },
      { style: "save", label: "Bookmark CTA", text: `Bookmark this thread. You'll need it.` },
    ];
  }

  // carousel
  return [
    { style: "engagement", label: "Slide CTA", text: band === "high" ? `Share this with someone who needs to see it.` : `Save this carousel for next time you hear this phrase.` },
    { style: "product", label: "Product CTA", text: `Test your own phrase at GreyRockAI.app` },
    { style: "save", label: "Save CTA", text: `Save → Share → Come back when you need the language for it.` },
  ];
}

export function generateGroundedScript(
  type: "expose" | "greyrock" | "educational",
  rawText: string,
  analysis: AnalysisResult,
  platform: Platform = "tiktok"
): string {
  const phrase = rawText.slice(0, 140);
  const pattern = formatPattern(analysis.primary_pattern);
  const secondary = analysis.secondary_pattern
    ? formatPattern(analysis.secondary_pattern)
    : null;
  const score = analysis.score;
  const band = getScoreBand(score);
  const nuance = analysis.nuance;
  const emoji = severityEmoji(band);
  const severity = severityLabel(band);
  const hashtags = buildHashtags(analysis, platform, type);

  if (type === "expose") {
    return generateExpose(phrase, pattern, secondary, score, band, nuance, emoji, severity, hashtags);
  }
  if (type === "greyrock") {
    return generateGreyRock(phrase, pattern, secondary, score, band, nuance, emoji, severity, hashtags);
  }
  return generateEducational(phrase, pattern, secondary, score, band, nuance, emoji, severity, hashtags);
}

function generateExpose(
  phrase: string, pattern: string, secondary: string | null,
  score: number, band: ScoreBand, nuance: string,
  emoji: string, severity: string, hashtags: string
): string {
  const intro = band === "high"
    ? `${emoji} "${phrase.slice(0, 60)}…" — this is ${pattern.toLowerCase()}, scoring ${score.toFixed(1)}/10.`
    : band === "moderate"
    ? `${emoji} "${phrase.slice(0, 60)}…" scored ${score.toFixed(1)}/10. Not an alarm — but it's worth seeing what this phrase does.`
    : `${emoji} "${phrase.slice(0, 60)}…" scored ${score.toFixed(1)}/10. Context determines whether this is a concern.`;

  const mechanism = nuance
    ? `\n\nWhat's happening:\n${nuance}`
    : `\n\nDetected pattern: ${pattern}${secondary ? ` with elements of ${secondary}` : ""}.`;

  const closer = band === "high"
    ? `\n\nThis is a clear pattern worth recognizing.`
    : band === "moderate"
    ? `\n\nThis may not be intentional, but the effect is still worth naming.`
    : `\n\nThis is likely safe communication — context and tone determine whether it warrants concern.`;

  return `${intro}\n\n"${phrase}"\n${mechanism}${closer}\n\n${hashtags}`;
}

function generateGreyRock(
  phrase: string, pattern: string, secondary: string | null,
  score: number, band: ScoreBand, nuance: string,
  emoji: string, severity: string, hashtags: string
): string {
  const setup = band === "high"
    ? `${emoji} How to respond when someone uses ${pattern.toLowerCase()}:`
    : band === "moderate"
    ? `${emoji} What to say back when you hear something like this:`
    : `${emoji} How to handle an ambiguous message like this:`;

  // Pattern-specific response suggestions
  const responseMap: Record<string, string> = {
    "Triangulation": "\"Other people's opinions aren't relevant to this conversation. Let's keep it between us.\"",
    "Social Leverage": "\"I'd rather focus on what you and I think, not unnamed others.\"",
    "Invalidation": "\"I hear you see it differently. My experience is still valid.\"",
    "Soft Invalidation": "\"My reaction is proportional to what happened. Let's focus on the issue.\"",
    "Gaslighting": "\"I remember what happened. I'm not going to debate my own experience.\"",
    "Guilt Induction": "\"I understand you feel that way. That doesn't change what I need right now.\"",
    "Self Victimization": "\"I'm not saying you're terrible. I'm asking to address this specific thing.\"",
    "Passive Aggression": "\"It sounds like there's something on your mind. I'd prefer you say it directly.\"",
    "Minimization": "\"The impact was real for me, regardless of how it was intended.\"",
    "Control Framing": "\"I respect your view, but I'm going to decide for myself.\"",
    "Blame Shift": "\"I want to stay on the original topic before we move to anything else.\"",
    "Coercive Withdrawal": "\"You can take space, but framing it as punishment won't change my position.\"",
    "False Consensus": "\"I'd need to hear from those people directly. Right now, this is between us.\"",
    "Consensus Weaponization": "\"Invoking other people doesn't change the conversation between you and me.\"",
  };

  const suggestedResponse = responseMap[pattern]
    || responseMap[secondary || ""]
    || "\"I hear you. I'll think about it and respond when I'm ready.\"";

  const why = band === "high"
    ? `This works against ${pattern.toLowerCase()} because it removes the emotional fuel without escalating.`
    : band === "moderate"
    ? `Against ${pattern.toLowerCase()}, this redirects without taking the bait — the pattern loses its leverage.`
    : `This keeps the exchange grounded. The ${pattern.toLowerCase()} framing doesn't get traction.`;

  return `${setup}\n\n"${phrase}"\n\nTry this:\n${suggestedResponse}\n\n${why}\n\nScore: ${score.toFixed(1)}/10 · Pattern: ${pattern}\n\n${hashtags}`;
}

function generateEducational(
  phrase: string, pattern: string, secondary: string | null,
  score: number, band: ScoreBand, nuance: string,
  emoji: string, severity: string, hashtags: string
): string {
  const title = band === "high"
    ? `${emoji} How ${pattern.toLowerCase()} works in everyday language:`
    : band === "moderate"
    ? `${emoji} What "${phrase.slice(0, 50)}…" is actually doing:`
    : `${emoji} Is this actually a problem? Let's break it down:`;

  // Build mechanism explanation — the core of the spoken breakdown
  const mechanism = nuance
    ? nuance
    : `This phrase uses ${pattern.toLowerCase()}${secondary ? ` combined with ${secondary.toLowerCase()}` : ""} to shift the focus away from the original issue.`;

  // Build the effect — what it does to the listener
  const effectMap: Record<string, string> = {
    "Triangulation": "It pulls in unnamed outside voices to make disagreement feel like you're the outlier.",
    "Social Leverage": "It frames unnamed agreement as proof, making your position feel isolated.",
    "Invalidation": "It labels your reaction as the problem instead of addressing what caused it.",
    "Soft Invalidation": "It frames your feeling as excessive, sidestepping the behavior that triggered it.",
    "Gaslighting": "It rewrites the shared reality so that your memory or perception becomes the thing being questioned.",
    "Guilt Induction": "It redirects accountability by making you feel responsible for the other person's emotional state.",
    "Self Victimization": "It flips the dynamic so the person being questioned becomes the injured party.",
    "Passive Aggression": "It embeds hostility in a neutral or positive surface, making it hard to address directly.",
    "Minimization": "It downsizes the impact of what happened, making concern feel like overreaction.",
    "Control Framing": "It presents a preference or demand as care, logic, or the only reasonable option.",
    "Blame Shift": "It redirects the conversation so the original issue is replaced by your behavior.",
    "Coercive Withdrawal": "It uses emotional distance or silence as a tool of pressure rather than genuine space.",
    "False Consensus": "It claims broad agreement that may not exist, making dissent feel unreasonable.",
    "Consensus Weaponization": "It uses group opinion — real or implied — as leverage to shut down individual perspective.",
  };

  const effect = effectMap[pattern]
    || `It shifts the dynamic in a way that can feel disorienting, even if unintentional.`;

  // Score context — calibrated to band
  const scoreContext = band === "high"
    ? `At ${score.toFixed(1)} out of 10, this is a strong signal. The mechanism is clear, even if the tone sounds casual.`
    : band === "moderate"
    ? `At ${score.toFixed(1)} out of 10, this sits in ambiguous territory — possibly careless, possibly strategic, but still ${pattern.toLowerCase() === "soft invalidation" || pattern.toLowerCase() === "invalidation" ? "dismissive" : "pressuring"} in effect.`
    : `At ${score.toFixed(1)} out of 10, this is low risk. The pattern is worth recognizing, but it doesn't necessarily signal intent.`;

  // Assemble — skip effect line if it largely repeats the mechanism
  const effectOverlaps = mechanism.toLowerCase().includes(effect.slice(3, 30).toLowerCase());
  const body = effectOverlaps
    ? `${mechanism}\n\n${scoreContext}`
    : `${mechanism}\n\n${effect}\n\n${scoreContext}`;

  return `${title}\n\n"${phrase}"\n\n${body}\n\nPattern: ${pattern}${secondary ? ` → ${secondary}` : ""}\n\n${hashtags}`;
}
